import { Loader2, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { ConfirmDialog } from "@shared/components/ConfirmDialog";
import { errMsg } from "@shared/api/errors";
import { useCommercialCosts, useUpdateCommercialCost } from "../api";
import { CommercialHeader } from "../components/CommercialHeader";
import { useCommercialStore } from "../store";
import type { CommercialCostForm, CommercialCostProfile, CommercialProfileKey } from "../types";

const labels: Record<CommercialProfileKey, string> = { auxiliar: "Auxiliar", supervisor: "Supervisor", directivo: "Directivo" };
const fields: Array<{ key: keyof CommercialCostForm; label: string; unit: "€" | "%" }> = [
  { key: "salario", label: "Salario", unit: "€" },
  { key: "coste_ss", label: "Coste Seguridad Social", unit: "€" },
  { key: "irpf_pct", label: "IRPF", unit: "%" },
  { key: "hardware", label: "Hardware", unit: "€" },
  { key: "office_365", label: "Microsoft Office 365", unit: "€" },
  { key: "antivirus", label: "Antivirus", unit: "€" },
  { key: "mantenimiento_red", label: "Mantenimiento de red", unit: "€" },
  { key: "adobe", label: "Adobe", unit: "€" },
  { key: "alquiler", label: "Alquiler", unit: "€" },
  { key: "limpieza", label: "Limpieza", unit: "€" },
  { key: "luz", label: "Luz", unit: "€" },
  { key: "telefonia_datos", label: "Telefonía y datos", unit: "€" },
  { key: "costes_estructura_pct", label: "Costes empresa (estructura)", unit: "%" },
];
const eur = new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" });

function toForm(profile: CommercialCostProfile): CommercialCostForm {
  return Object.fromEntries(fields.map(({ key }) => [key, profile[key]])) as CommercialCostForm;
}

function calculate(form: CommercialCostForm): number {
  const base = form.salario + form.coste_ss + form.hardware + form.office_365 + form.antivirus + form.mantenimiento_red + form.adobe + form.alquiler + form.limpieza + form.luz + form.telefonia_datos;
  const irpf = form.salario * (form.irpf_pct / 100);
  const estructura = base * (form.costes_estructura_pct / 100);
  return Math.round((base + irpf + estructura) * 100) / 100;
}

export function CommercialCostsPage() {
  const { emisorId } = useCommercialStore();
  const { data: profiles = [], isLoading } = useCommercialCosts(emisorId);
  const updateMut = useUpdateCommercialCost();
  const [selected, setSelected] = useState<CommercialProfileKey>("auxiliar");
  const [form, setForm] = useState<CommercialCostForm | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const profile = profiles.find((item) => item.perfil === selected) ?? null;
  useEffect(() => { if (profile) setForm(toForm(profile)); }, [profile]);

  async function save() {
    if (!emisorId || !profile || !form || updateMut.isPending) return;
    setError(null);
    try {
      await updateMut.mutateAsync({ emisorId, id: profile.id, payload: form });
    } catch (e) {
      setError(errMsg(e, "No se pudieron guardar los costes."));
    }
  }

  return (
    <div className="space-y-6 p-6 lg:p-8">
      <CommercialHeader title="Costes" subtitle="Estructura de coste de Auxiliar, Supervisor y Directivo por empresa." />
      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900">El total calculado se utiliza como coste/hora vigente en nuevas valoraciones. Las valoraciones guardadas conservan su snapshot histórico.</div>

      <div className="flex flex-wrap gap-2">
        {(Object.keys(labels) as CommercialProfileKey[]).map((key) => (
          <button key={key} type="button" onClick={() => setSelected(key)} disabled={updateMut.isPending} className={selected === key ? "rounded-xl bg-emerald-700 px-4 py-2 text-sm font-black text-white disabled:cursor-wait disabled:opacity-50" : "rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-600 hover:bg-slate-50 disabled:cursor-wait disabled:opacity-50"}>{labels[key]}</button>
        ))}
      </div>

      {isLoading || !form || !profile ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-sm font-semibold text-slate-500">Cargando costes…</div>
      ) : (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 md:flex-row md:items-end md:justify-between"><div><p className="text-xs font-black uppercase tracking-wide text-emerald-700">Perfil</p><h2 className="text-xl font-black text-slate-900">{labels[selected]}</h2></div><div className="rounded-xl bg-emerald-50 px-4 py-3 text-right"><p className="text-xs font-black uppercase tracking-wide text-emerald-700">Total calculado</p><p className="text-2xl font-black text-emerald-900">{eur.format(calculate(form))}</p></div></div>
          <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {fields.map(({ key, label, unit }) => (
              <label key={key} className="text-xs font-black uppercase tracking-wide text-slate-500">{label}<div className="mt-1 flex overflow-hidden rounded-xl border border-slate-200 focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-500/15"><input type="number" min="0" step="0.01" value={form[key]} onChange={(e) => setForm((current) => current ? { ...current, [key]: Number(e.target.value) } : current)} className="min-w-0 flex-1 px-3 py-2.5 text-right text-sm font-semibold outline-none" /><span className="grid min-w-10 place-items-center border-l border-slate-200 bg-slate-50 text-sm font-black text-slate-500">{unit}</span></div></label>
            ))}
          </div>
          {error && <div className="mt-4 rounded-xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">{error}</div>}
          {updateMut.isPending && <div className="mt-4 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-800"><Loader2 className="animate-spin" size={17} />Actualizando costes…</div>}
          <div className="mt-5 flex justify-end"><button type="button" onClick={() => setConfirmOpen(true)} disabled={updateMut.isPending} className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-black text-white hover:bg-emerald-800 disabled:cursor-wait disabled:opacity-50">{updateMut.isPending ? <Loader2 className="animate-spin" size={17} /> : <Save size={17} />}{updateMut.isPending ? "Guardando…" : "Guardar costes"}</button></div>
        </section>
      )}

      <ConfirmDialog open={confirmOpen} title={`Guardar costes de ${labels[selected]}`} message="¿Confirmas la edición de esta estructura de costes? Las nuevas valoraciones usarán el nuevo total; las anteriores no cambiarán." confirmLabel="Guardar cambios" onConfirm={() => void save()} onClose={() => setConfirmOpen(false)} />
    </div>
  );
}
